import { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import Loading from '../loading/Loading';
import { getComunidadBankStatements, setStatementsToken, uploadComunidadBankStatement } from '../../controllers/statements';
import { useAuth } from '../auth/AuthProvider';

const monthNames = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' }
];

function monthLabel(monthNumber) {
  return monthNames.find(m => m.value === monthNumber)?.label || '-';
}

function getCicloMonths(ciclo) {
  // Ciclo starts in August of `ciclo` year and ends in July of `ciclo+1`
  return [
    { value: 8, label: `Agosto ${ciclo}` },
    { value: 9, label: `Septiembre ${ciclo}` },
    { value: 10, label: `Octubre ${ciclo}` },
    { value: 11, label: `Noviembre ${ciclo}` },
    { value: 12, label: `Diciembre ${ciclo}` },
    { value: 1, label: `Enero ${ciclo + 1}` },
    { value: 2, label: `Febrero ${ciclo + 1}` },
    { value: 3, label: `Marzo ${ciclo + 1}` },
    { value: 4, label: `Abril ${ciclo + 1}` },
    { value: 5, label: `Mayo ${ciclo + 1}` },
    { value: 6, label: `Junio ${ciclo + 1}` },
    { value: 7, label: `Julio ${ciclo + 1}` },
  ];
}

function getYearOptions(selectedYear, ciclo) {
  const now = new Date();
  const maxCiclo = now.getMonth() + 1 >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  const years = new Set();

  for (let year = maxCiclo; year >= 2025; year--) {
    years.add(year);
  }

  if (ciclo && ciclo >= 2025 && ciclo <= maxCiclo) {
    years.add(ciclo);
  }
  if (selectedYear && selectedYear >= 2025 && selectedYear <= maxCiclo) {
    years.add(selectedYear);
  }

  return Array.from(years).sort((a, b) => b - a);
}

function formatBytes(bytes) {
  if (!bytes || bytes < 1024) {
    return `${bytes || 0} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(dateString) {
  if (!dateString) {
    return '-';
  }
  const date = new Date(dateString);
  return date.toLocaleString('es-MX');
}

function ComunidadStatements({ comunidadId, ciclo }) {
  const { token } = useAuth();
  const [statements, setStatements] = useState([]);
  const [report, setReport] = useState();
  const [file, setFile] = useState();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const now = new Date();
  const currentCiclo = now.getMonth() + 1 >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  const [selectedYear, setSelectedYear] = useState(ciclo || currentCiclo);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState();
  const yearOptions = getYearOptions(selectedYear, ciclo);

  setStatementsToken(token);

  useEffect(() => {
    if (ciclo) {
      setSelectedYear(ciclo);
    }
  }, [ciclo]);

  useEffect(() => {
    loadStatements();
  }, [comunidadId, selectedYear]);

  async function loadStatements() {
    setLoading(true);
    setMessage(undefined);
    try {
      const data = await getComunidadBankStatements(comunidadId, selectedYear);
      setStatements(data.statements || []);
      setReport(data.report);
    }
    catch (e) {
      setMessage({
        type: 'danger',
        text: e?.message || 'No se pudieron cargar los estados de cuenta.'
      });
    }
    setLoading(false);
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!file) {
      setMessage({
        type: 'warning',
        text: 'Selecciona un archivo para subir.'
      });
      return;
    }
    if (!month) {
      setMessage({
        type: 'warning',
        text: 'Selecciona el mes correspondiente al estado de cuenta.'
      });
      return;
    }
    if (!selectedYear) {
      setMessage({
        type: 'warning',
        text: 'Selecciona el año correspondiente al estado de cuenta.'
      });
      return;
    }

    setUploading(true);
    setMessage(undefined);

    try {
      await uploadComunidadBankStatement(comunidadId, selectedYear, month, file);
      setFile(undefined);
      const input = document.getElementById('estado-cuenta-file-input');
      if (input) {
        input.value = '';
      }
      setMessage({
        type: 'success',
        text: 'Estado de cuenta cargado con éxito.'
      });
      await loadStatements();
    }
    catch (e) {
      setMessage({
        type: 'danger',
        text: e?.message || 'Ocurrió un error al subir el archivo.'
      });
    }

    setUploading(false);
  }

  if (loading) {
    return <Loading text="Cargando estados de cuenta..." />;
  }

  return (
    <Row className='mt-3'>
      <Col>
        <Card>
          <Card.Body>
            <h3>Estados de Cuenta</h3>
            <p>Sube aquí los estados de cuenta bancarios de la comunidad.</p>

            {message && (
              <Alert variant={message.type} dismissible onClose={() => setMessage(undefined)}>
                {message.text}
              </Alert>
            )}

            <Form onSubmit={onSubmit} className='mb-4'>
              <Row className='align-items-end'>
                <Col md={2}>
                  <Form.Label>Año</Form.Label>
                  <Form.Select
                    value={selectedYear}
                    onChange={(ev) => setSelectedYear(parseInt(ev.target.value))}
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label>Mes</Form.Label>
                  <Form.Select
                    value={month}
                    onChange={(ev) => setMonth(parseInt(ev.target.value))}
                  >
                    {getCicloMonths(selectedYear).map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Label>Archivo</Form.Label>
                  <Form.Control
                    id='estado-cuenta-file-input'
                    type='file'
                    accept='.pdf,.png,.jpg,.jpeg'
                    onChange={(ev) => setFile(ev.target.files?.[0])}
                  />
                  <Form.Text className='text-muted'>
                    Formatos permitidos: PDF, PNG, JPG/JPEG. Tamaño máximo: 10MB.
                  </Form.Text>
                </Col>
                <Col md={3}>
                  <Button type='submit' disabled={uploading || !file}>
                    {uploading ? 'Subiendo...' : 'Subir estado de cuenta'}
                  </Button>
                </Col>
              </Row>
            </Form>

            {statements.length === 0 && <p>No hay estados de cuenta cargados para el año seleccionado.</p>}

            {statements.length > 0 && (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Fecha de captura</th>
                    <th>Ciclo</th>
                    <th>Mes</th>
                    <th>Archivo</th>
                    <th>Tamaño</th>
                    <th>Subido por</th>
                  </tr>
                </thead>
                <tbody>
                  {statements.map((statement) => (
                    <tr key={statement.id}>
                      <td>{formatDate(statement.createdAt)}</td>
                      <td>{statement.ciclo} - {statement.ciclo + 1}</td>
                      <td>{monthLabel(statement.month)}</td>
                      <td>
                        <a href={statement.url} target='_blank' rel='noreferrer'>
                          {statement.fileName}
                        </a>
                      </td>
                      <td>{formatBytes(statement.size)}</td>
                      <td>{statement.uploadedBy?.email || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            {report && (
              <Row>
                <Col md={6}>
                  <p><strong>Meses cargados:</strong> {report.uploadedMonths?.map(monthLabel).join(', ') || 'Ninguno'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Meses faltantes:</strong> {report.missingMonths?.map(monthLabel).join(', ') || 'Ninguno'}</p>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}

export default ComunidadStatements;
